<template>
  <section :class="$style.section">
    <div :class="$style.header">
      <h2 :class="$style.title">Параметры ТС</h2>
      <div v-if="clientBrands.length" :class="$style.brands">
        <button
          v-for="brand in clientBrands"
          :key="`${brand.carIndex}-${brand.title}`"
          type="button"
          :class="[
            $style.brand,
            { [$style.active]: selectedCarIdx === brand.carIndex },
          ]"
          @click="selectBrand(brand)"
        >
          {{ brand.title }}
        </button>
      </div>
    </div>

    <div :class="$style.fields">
      <div
        :class="[
          $style.field,
          { [$style.active]: isVehicleFieldActive('make') },
        ]"
      >
        <input
          ref="makeInput"
          v-model="carMake"
          type="text"
          placeholder="Марка"
          readonly
          @focus="onVehicleFieldFocus('make')"
          @input="handleMakeInput"
          @blur="onVehicleFieldBlur('make')"
        />
        <button
          type="button"
          :class="$style.selectTrigger"
          aria-label="Открыть список марок"
          @mousedown.prevent
          @click="focusVehicleField('make')"
        >
          <img
            src="/icons/vehicle-select-arrow.svg"
            width="24"
            height="24"
            alt=""
          />
        </button>
        <button
          v-if="car.make"
          type="button"
          :class="$style.clear"
          aria-label="Очистить марку"
          @click="clearCarField('make')"
        />
        <div v-if="makes.length" :class="$style.hints">
          <button
            v-for="make in makes"
            :key="make.id"
            type="button"
            :class="$style.hint"
            @click="handleSetMake(make)"
          >
            {{ make.title }}
          </button>
        </div>
      </div>

      <div
        :class="[
          $style.field,
          {
            [$style.active]: isVehicleFieldActive('model'),
            [$style.disabled]: isVehicleFieldDisabled('model'),
          },
        ]"
      >
        <input
          ref="modelInput"
          v-model="carModel"
          type="text"
          readonly
          :placeholder="order.car?.make ? 'Модель' : 'Сначала укажите марку'"
          :disabled="isVehicleFieldDisabled('model')"
          @focus="onVehicleFieldFocus('model')"
          @input="handleModelInput"
          @blur="onVehicleFieldBlur('model')"
        />
        <button
          v-if="!isVehicleFieldDisabled('model')"
          type="button"
          :class="$style.selectTrigger"
          aria-label="Открыть список моделей"
          @mousedown.prevent
          @click="focusVehicleField('model')"
        >
          <img
            src="/icons/vehicle-select-arrow.svg"
            width="24"
            height="24"
            alt=""
          />
        </button>
        <button
          v-if="car.model"
          type="button"
          :class="$style.clear"
          aria-label="Очистить модель"
          @click="clearCarField('model')"
        />
        <div v-if="models.length" :class="$style.hints">
          <button
            v-for="model in models"
            :key="model.id"
            type="button"
            :class="$style.hint"
            @click="handleSetModel(model)"
          >
            {{ model.title }}
          </button>
        </div>
      </div>

      <div
        :class="[
          $style.field,
          {
            [$style.active]: isVehicleFieldActive('generation'),
            [$style.disabled]: isVehicleFieldDisabled('generation'),
          },
        ]"
      >
        <input
          ref="generationInput"
          v-model="carGeneration"
          type="text"
          :placeholder="
            order.car?.model ? 'Поколение' : 'Сначала укажите модель'
          "
          readonly
          :disabled="isVehicleFieldDisabled('generation')"
          @focus="onVehicleFieldFocus('generation')"
          @input="handleGenerationInput"
          @blur="onVehicleFieldBlur('generation')"
        />
        <button
          v-if="!isVehicleFieldDisabled('generation')"
          type="button"
          :class="$style.selectTrigger"
          aria-label="Открыть список поколений"
          @mousedown.prevent
          @click="focusVehicleField('generation')"
        >
          <img
            src="/icons/vehicle-select-arrow.svg"
            width="24"
            height="24"
            alt=""
          />
        </button>
        <button
          v-if="car.generation"
          type="button"
          :class="$style.clear"
          aria-label="Очистить поколение"
          @click="clearCarField('generation')"
        />
        <div v-if="generations.length" :class="$style.hints">
          <button
            v-for="generation in generations"
            :key="generation.id"
            type="button"
            :class="$style.hint"
            @click="handleSetGeneration(generation)"
          >
            {{ generation.title }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="order.car?.generation" :class="$style.tireSection">
      <div :class="$style.tireHeader">
        <h3 :class="$style.tireTitle">Типоразмер</h3>
        <div v-if="standardTires.length" :class="$style.tireChips">
          <button
            v-for="tire in standardTires"
            :key="tire.id"
            type="button"
            :class="[
              $style.tireChip,
              { [$style.active]: selectedTireId === tire.id },
            ]"
            @click="selectTire(tire)"
          >
            {{ tire.title }}
          </button>
        </div>
      </div>

      <div :class="$style.tireHeader">
        <h3 :class="$style.tireTitle">Дополнительно</h3>
        <div :class="$style.tireChips">
          <button
            type="button"
            :class="[
              $style.extraChip,
              { [$style.activeBlue]: includeRunflat },
            ]"
            @click="toggleRunflat"
          >
            Runflat
          </button>
          <button
            type="button"
            :class="[
              $style.extraChip,
              { [$style.activeBlue]: includePressureSensor },
            ]"
            @click="togglePressureSensor"
          >
            Датчик
          </button>
          <button
            type="button"
            :class="[
              $style.extraChip,
              { [$style.activeBlue]: includeAlloyWheel },
            ]"
            @click="toggleAlloyWheel"
          >
            Литой диск
          </button>
        </div>
      </div>

      <div v-if="isWideWheelState" :class="[$style.wheel, $style.wheelWide]">
        <TireSizeWheelWide
          :primary-label="primaryWheelLabel"
          :secondary-label="secondaryWheelLabel"
          :secondary-placeholder="secondaryWheelPlaceholder"
        />
      </div>
      <div v-else :class="$style.wheel">
        <TireSizeWheel :class="$style.wheelItem" :label="primaryWheelLabel" />
      </div>

      <div :class="$style.tireInfo">
        <div :class="$style.tireInfoText">
          <span :class="$style.infoIcon" aria-hidden="true" />
          <p>{{ tireInfoMessage }}</p>
        </div>
        <transition name="picker-expand">
          <div v-if="isTirePickerVisible" :class="$style.picker">
            <div v-if="additionalTires.length" :class="$style.pickerChips">
              <button
                v-for="tire in additionalTires"
                :key="tire.id"
                type="button"
                :class="[
                  $style.tireChip,
                  { [$style.active]: selectedTireId === tire.id },
                ]"
                @click="selectStandardTireInPicker(tire)"
              >
                {{ tire.title }}
              </button>
            </div>

            <div :class="$style.paramLabels">
              <span>Ширина</span>
              <span>Профиль</span>
              <span>Диаметр</span>
            </div>

            <div :class="$style.paramRow">
              <div :class="$style.paramField">
                <button
                  type="button"
                  :class="[
                    $style.paramBox,
                    { [$style.open]: openParamField === 'primary-width' },
                  ]"
                  @click="toggleParamField('primary-width')"
                >
                  <span>{{ pickerPrimary.width || "XX" }}</span>
                </button>
                <div
                  v-if="openParamField === 'primary-width'"
                  :class="$style.paramOptions"
                >
                  <button
                    v-for="option in primaryWidthOptions"
                    :key="option"
                    type="button"
                    :class="$style.paramOption"
                    @click="
                      selectParamOption(
                        'pickerPrimary',
                        'width',
                        option,
                        'normalizePrimarySelection'
                      )
                    "
                  >
                    {{ option }}
                  </button>
                </div>
              </div>
              <span :class="$style.paramDivider">/</span>
              <div :class="$style.paramField">
                <button
                  type="button"
                  :class="[
                    $style.paramBox,
                    { [$style.open]: openParamField === 'primary-profile' },
                  ]"
                  @click="toggleParamField('primary-profile')"
                >
                  <span>{{ pickerPrimary.profile || "XX" }}</span>
                </button>
                <div
                  v-if="openParamField === 'primary-profile'"
                  :class="$style.paramOptions"
                >
                  <button
                    v-for="option in primaryProfileOptions"
                    :key="option"
                    type="button"
                    :class="$style.paramOption"
                    @click="
                      selectParamOption(
                        'pickerPrimary',
                        'profile',
                        option,
                        'normalizePrimarySelection'
                      )
                    "
                  >
                    {{ option }}
                  </button>
                </div>
              </div>
              <span :class="$style.paramLetter">R</span>
              <div :class="$style.paramField">
                <button
                  type="button"
                  :class="[
                    $style.paramBox,
                    { [$style.open]: openParamField === 'primary-diameter' },
                  ]"
                  @click="toggleParamField('primary-diameter')"
                >
                  <span>{{ pickerPrimary.diameter || "XX" }}</span>
                </button>
                <div
                  v-if="openParamField === 'primary-diameter'"
                  :class="$style.paramOptions"
                >
                  <button
                    v-for="option in primaryDiameterOptions"
                    :key="option"
                    type="button"
                    :class="$style.paramOption"
                    @click="
                      selectParamOption(
                        'pickerPrimary',
                        'diameter',
                        option,
                        'normalizePrimarySelection'
                      )
                    "
                  >
                    {{ option }}
                  </button>
                </div>
              </div>
            </div>

            <div v-if="isWideTires" :class="$style.paramRow">
              <div :class="$style.paramField">
                <button
                  type="button"
                  :class="[
                    $style.paramBox,
                    { [$style.open]: openParamField === 'secondary-width' },
                  ]"
                  @click="toggleParamField('secondary-width')"
                >
                  <span
                    :class="{ [$style.placeholder]: !pickerSecondary.width }"
                  >
                    {{ pickerSecondary.width || "XX" }}
                  </span>
                </button>
                <div
                  v-if="openParamField === 'secondary-width'"
                  :class="$style.paramOptions"
                >
                  <button
                    v-for="option in secondaryWidthOptions"
                    :key="option"
                    type="button"
                    :class="$style.paramOption"
                    @click="
                      selectParamOption(
                        'pickerSecondary',
                        'width',
                        option,
                        'normalizeSecondarySelection'
                      )
                    "
                  >
                    {{ option }}
                  </button>
                </div>
              </div>
              <span :class="$style.paramDivider">/</span>
              <div :class="$style.paramField">
                <button
                  type="button"
                  :class="[
                    $style.paramBox,
                    { [$style.open]: openParamField === 'secondary-profile' },
                  ]"
                  @click="toggleParamField('secondary-profile')"
                >
                  <span
                    :class="{ [$style.placeholder]: !pickerSecondary.profile }"
                  >
                    {{ pickerSecondary.profile || "XX" }}
                  </span>
                </button>
                <div
                  v-if="openParamField === 'secondary-profile'"
                  :class="$style.paramOptions"
                >
                  <button
                    v-for="option in secondaryProfileOptions"
                    :key="option"
                    type="button"
                    :class="$style.paramOption"
                    @click="
                      selectParamOption(
                        'pickerSecondary',
                        'profile',
                        option,
                        'normalizeSecondarySelection'
                      )
                    "
                  >
                    {{ option }}
                  </button>
                </div>
              </div>
              <span :class="$style.paramLetter">R</span>
              <div :class="$style.paramField">
                <button
                  type="button"
                  :class="[
                    $style.paramBox,
                    { [$style.open]: openParamField === 'secondary-diameter' },
                  ]"
                  @click="toggleParamField('secondary-diameter')"
                >
                  <span
                    :class="{ [$style.placeholder]: !pickerSecondary.diameter }"
                  >
                    {{ pickerSecondary.diameter || "XX" }}
                  </span>
                </button>
                <div
                  v-if="openParamField === 'secondary-diameter'"
                  :class="$style.paramOptions"
                >
                  <button
                    v-for="option in secondaryDiameterOptions"
                    :key="option"
                    type="button"
                    :class="$style.paramOption"
                    @click="
                      selectParamOption(
                        'pickerSecondary',
                        'diameter',
                        option,
                        'normalizeSecondarySelection'
                      )
                    "
                  >
                    {{ option }}
                  </button>
                </div>
              </div>
            </div>

            <div :class="$style.pickerMeta">
              <button
                type="button"
                :class="[$style.wideToggle, { [$style.active]: isWideTires }]"
                @click="toggleWideTires"
              >
                <span
                  :class="[$style.wideMarker, { [$style.active]: isWideTires }]"
                />
                <span>Разноширокие</span>
              </button>
            </div>
          </div>
        </transition>
        <button
          v-if="!isTirePickerVisible"
          type="button"
          :class="$style.tirePickerBtn"
          @click="openTirePicker"
        >
          <span>Задать параметры шин</span>
          <span :class="$style.tirePickerArrow" aria-hidden="true" />
        </button>
      </div>
    </div>
  </section>
</template>

<script>
import TireSizeWheel from "@/components/requests/TireSizeWheel.vue";
import TireSizeWheelWide from "@/components/requests/TireSizeWheelWide.vue";
import { mapState } from "vuex";
import clientSearchMixin from "@/mixins/clientSearchMixin";
import vehicleSearchMixin from "@/mixins/vehicleSearchMixin";

export default {
  name: "VehicleParametersSection",
  components: {
    TireSizeWheel,
    TireSizeWheelWide,
  },
  mixins: [clientSearchMixin, vehicleSearchMixin],
  props: {
    initialClient: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      isInitialized: false,
      openParamField: "",
      isClientLoading: false,
      openedVehicleField: "",
    };
  },
  computed: {
    ...mapState({
      order: (state) => state.orders.order,
      car: (state) => state.orders.car,
    }),
    clientSource() {
      return this.initialClient || this.client;
    },
    clientCarOptions() {
      return this.getClientCarBrands(this.clientSource);
    },
    clientBrands() {
      const brands = this.clientCarOptions;
      if (brands.length <= 1) {
        return [];
      }

      if (this.selectedCarIdx !== null) {
        return brands.filter((brand) => brand.carIndex !== this.selectedCarIdx);
      }

      const currentNumber = this.normalizeCarNumberValue(this.order?.car?.number);
      if (!currentNumber) {
        return brands;
      }

      const matchedIndex = this.findClientCarByNumber(
        brands.map((item) => item.car),
        currentNumber,
      );
      if (matchedIndex < 0) {
        return brands;
      }

      return brands.filter((brand) => brand.carIndex !== matchedIndex);
    },
    nextVehicleField() {
      if (!this.order.car?.make) {
        return "make";
      }

      if (!this.order.car?.model) {
        return "model";
      }

      if (!this.order.car?.generation) {
        return "generation";
      }

      return "";
    },
    carMake: {
      get() {
        return this.car.make;
      },
      set(value) {
        this.$store.commit("orders/setCarField", { field: "make", value });
      },
    },
    carModel: {
      get() {
        return this.car.model;
      },
      set(value) {
        this.$store.commit("orders/setCarField", { field: "model", value });
      },
    },
    carGeneration: {
      get() {
        return this.car.generation;
      },
      set(value) {
        this.$store.commit("orders/setCarField", {
          field: "generation",
          value,
        });
      },
    },
    activeTireGroup() {
      return this.tireGroups.find(
        (item) => String(item.diameter) === String(this.activeTireGroupDiameter)
      );
    },
    parsedPickerTires() {
      return this.getParsedPickerTires();
    },
    primaryWidthOptions() {
      return this.getPickerFieldOptions(this.pickerPrimary, "width");
    },
    primaryProfileOptions() {
      return this.getPickerFieldOptions(this.pickerPrimary, "profile");
    },
    primaryDiameterOptions() {
      return this.getPickerFieldOptions(this.pickerPrimary, "diameter");
    },
    secondaryWidthOptions() {
      return this.getPickerFieldOptions(this.pickerSecondary, "width");
    },
    secondaryProfileOptions() {
      return this.getPickerFieldOptions(this.pickerSecondary, "profile");
    },
    secondaryDiameterOptions() {
      return this.getPickerFieldOptions(this.pickerSecondary, "diameter");
    },
    selectedPrimaryTire() {
      return this.findPickerTire(this.pickerPrimary);
    },
    isWideWheelState() {
      return (
        this.isWideTires ||
        Boolean(
          this.order?.car?.tires?.summer_tires?.second_tires ||
            this.order?.car?.tires?.second_tires
        ) ||
        Boolean(this.car.second_tires)
      );
    },
    primaryWheelLabel() {
      const primaryTire =
        this.getTireTitle(this.findSelectedTire()) ||
        this.getTireTitle(this.selectedPrimaryTire) ||
        this.car.first_tires;
      const secondaryTire =
        this.getTireTitle(this.findPickerTire(this.pickerSecondary)) ||
        this.car.second_tires;

      if (
        this.isWideWheelState &&
        this.compareWheelLabels(primaryTire, secondaryTire) < 0
      ) {
        return this.formatWheelLabel(secondaryTire);
      }

      return this.formatWheelLabel(primaryTire);
    },
    secondaryWheelLabel() {
      if (!this.isWideWheelState) {
        return "";
      }

      const primaryTire =
        this.getTireTitle(this.findSelectedTire()) ||
        this.getTireTitle(this.selectedPrimaryTire) ||
        this.car.first_tires;
      const secondaryTire =
        this.getTireTitle(this.findPickerTire(this.pickerSecondary)) ||
        this.car.second_tires;

      if (this.compareWheelLabels(primaryTire, secondaryTire) < 0) {
        return this.formatWheelLabel(primaryTire);
      }

      return this.formatWheelLabel(secondaryTire);
    },
    secondaryWheelPlaceholder() {
      return this.isWideWheelState && !this.secondaryWheelLabel
        ? "XX/XX RXX"
        : "";
    },
    hasSelectedTireSize() {
      return Boolean(
        this.selectedTireId ||
          this.order?.car?.tires?.summer_tires?.first_tires ||
          this.order?.car?.tires?.first_tires ||
          this.car.first_tires
      );
    },
    isTirePickerVisible() {
      return (
        this.isTirePickerOpen &&
        (this.tireGroups.length > 0 ||
          this.standardTires.length > 0 ||
          this.additionalTires.length > 0)
      );
    },
    tireInfoMessage() {
      if (!this.hasSelectedTireSize) {
        return "Укажите необходимые параметры, или выберите один из предложенных вариантов.";
      }

      return "Если указанный размер не соответствует, укажите необходимые параметры, или выберите один из предложенных вариантов.";
    },
  },
  watch: {
    initialClient: {
      immediate: true,
      async handler(client) {
        await this.ensureClientSource(client);
      },
    },
    "order.car.generation": {
      immediate: true,
      handler(generationId) {
        this.ensureStandardTires(generationId);
      },
    },
    "order.client": {
      immediate: true,
      async handler(clientId) {
        if (!clientId || this.getActiveClientCars(this.clientSource).length) {
          return;
        }
        await this.ensureClientSource(this.clientSource || { id: clientId });
      },
    },
  },
  methods: {
    isVehicleFieldDisabled(field) {
      if (field === "model") {
        return !this.order.car?.make;
      }

      if (field === "generation") {
        return !this.order.car?.model;
      }

      return false;
    },
    isVehicleFieldActive(field) {
      if (this.isVehicleFieldDisabled(field)) {
        return false;
      }

      return (
        this.openedVehicleField === field ||
        this.nextVehicleField === field ||
        Boolean(this.car[field])
      );
    },
    focusVehicleField(field) {
      if (this.isVehicleFieldDisabled(field)) {
        return;
      }

      this.$nextTick(() => {
        this.$refs[`${field}Input`]?.focus();
      });
    },
    openVehicleFieldList(field) {
      if (field === "make") {
        this.searchMakes();
        return;
      }

      if (field === "model") {
        this.searchModels();
        return;
      }

      this.searchGenerations();
    },
    closeVehicleFieldList(field) {
      if (field === "make") {
        this.resetMakes();
        return;
      }

      if (field === "model") {
        this.resetModels();
        return;
      }

      this.resetGenerations();
    },
    onVehicleFieldFocus(field) {
      if (this.isVehicleFieldDisabled(field)) {
        return;
      }

      this.openedVehicleField = field;
      this.openVehicleFieldList(field);
    },
    onVehicleFieldBlur(field) {
      setTimeout(() => {
        if (this.openedVehicleField === field) {
          this.openedVehicleField = "";
        }
      }, 300);

      this.closeVehicleFieldList(field);
    },
    async ensureClientSource(client) {
      const sourceClient = client || this.clientSource;
      const cars = this.getActiveClientCars(sourceClient);

      if (sourceClient) {
        this.client = sourceClient;
      }

      if (cars.length) {
        if (!this.isInitialized) {
          await this.initializeClientCars();
        }
        return;
      }

      const clientId = sourceClient?.id || this.order?.client;
      if (!clientId || this.isClientLoading) return;

      this.isClientLoading = true;
      const fullClient = await this.getFullClient(sourceClient || clientId);
      this.isClientLoading = false;

      if (!fullClient) return;

      this.client = fullClient;
      if (!this.isInitialized && this.getActiveClientCars(fullClient).length) {
        await this.initializeClientCars();
      }
    },
    resetSelectedClientCar() {
      this.selectedCarIdx = null;
    },
    handleMakeInput() {
      this.resetSelectedClientCar();
      this.openedVehicleField = "make";
      this.searchMakes();
    },
    handleModelInput() {
      this.resetSelectedClientCar();
      this.openedVehicleField = "model";
      this.searchModels();
    },
    handleGenerationInput() {
      this.resetSelectedClientCar();
      this.openedVehicleField = "generation";
      this.searchGenerations();
    },
    async handleSetMake(make) {
      this.resetSelectedClientCar();
      await this.setMake(make, { allowAutoSelectNext: true });
      if (!this.order.car?.model) {
        this.focusVehicleField("model");
        return;
      }
      if (!this.order.car?.generation) {
        this.focusVehicleField("generation");
      }
    },
    async handleSetModel(model) {
      this.resetSelectedClientCar();
      await this.setModel(model, { allowAutoSelectNext: true });
      if (!this.order.car?.generation) {
        this.focusVehicleField("generation");
      }
    },
    async handleSetGeneration(generation) {
      this.resetSelectedClientCar();
      await this.setGeneration(generation);
    },
    clearCarField(field) {
      this.resetSelectedClientCar();
      if (field === "make") {
        this.clearMake();
        this.openedVehicleField = "make";
        return;
      }
      if (field === "model") {
        this.clearModel();
        this.openedVehicleField = "model";
        return;
      }
      this.clearGeneration();
      this.openedVehicleField = "generation";
    },
    toggleParamField(field) {
      this.openParamField = this.openParamField === field ? "" : field;
    },
    getPickerOptionsSortValue(value) {
      return Number(String(value || "").replace(",", "."));
    },
    getPickerFieldOptions(selection, field) {
      return [
        ...new Set(
          this.parsedPickerTires
            .filter((item) =>
              ["width", "profile", "diameter"].every((key) => {
                if (key === field) {
                  return true;
                }

                const selectedValue = String(selection?.[key] || "");
                return !selectedValue || item[key] === selectedValue;
              })
            )
            .map((item) => item[field])
            .filter(Boolean)
        ),
      ].sort((first, second) => {
        const firstValue = this.getPickerOptionsSortValue(first);
        const secondValue = this.getPickerOptionsSortValue(second);
        return firstValue - secondValue || String(first).localeCompare(String(second), "ru");
      });
    },
    normalizePickerSelection(target, preferredKey = "") {
      const keys = ["width", "profile", "diameter"];
      const nextSelection = { ...(this[target] || {}) };
      let hasChanges = false;
      let shouldRecheck = true;

      while (shouldRecheck) {
        shouldRecheck = false;

        keys.forEach((key) => {
          if (key === preferredKey || !nextSelection[key]) {
            return;
          }

          const options = this.getPickerFieldOptions(nextSelection, key);
          if (!options.includes(String(nextSelection[key]))) {
            nextSelection[key] = "";
            hasChanges = true;
            shouldRecheck = true;
          }
        });
      }

      if (hasChanges) {
        this[target] = nextSelection;
      }
    },
    async selectStandardTireInPicker(tire) {
      this.pickerPrimary = this.getSelectionFromTire(tire);
      await this.selectTire(tire);
    },
    selectParamOption(target, key, value, normalizerName) {
      this[target] = {
        ...(this[target] || {}),
        [key]: String(value),
      };

      if (typeof this[normalizerName] === "function") {
        this[normalizerName](key);
      }
      this.openParamField = "";
      if (this.isPickerSelectionComplete(this[target])) {
        this.confirmTirePicker({ closePicker: false });
      }
    },
    isPickerSelectionComplete(selection) {
      return Boolean(
        selection?.width && selection?.profile && selection?.diameter
      );
    },
    normalizePrimarySelection(preferredKey = "") {
      this.normalizePickerSelection("pickerPrimary", preferredKey);
    },
    normalizeSecondarySelection(preferredKey = "") {
      this.normalizePickerSelection("pickerSecondary", preferredKey);
    },
    async initializeClientCars() {
      this.isInitialized = true;
      const cars = this.getActiveClientCars(this.clientSource);
      if (!cars.length) {
        return;
      }

      if (cars.length === 1) {
        await this.applyClientCar(cars[0], 0);
        return;
      }

      const matchedIdx = this.findSelectedClientCarIndex(cars, this.order?.car);
      if (matchedIdx < 0) {
        return;
      }

      await this.applyClientCar(cars[matchedIdx], matchedIdx);
    },
    async selectBrand(brand) {
      await this.applyClientCar(brand.car, brand.carIndex);
    },
    findSelectedTire() {
      return [...(this.standardTires || []), ...(this.additionalTires || [])].find(
        (item) => item.id === this.selectedTireId
      );
    },
    compareWheelLabels(firstLabel, secondLabel) {
      const first = this.parseMetricTireTitle(firstLabel);
      const second = this.parseMetricTireTitle(secondLabel);

      if (!first || !second) {
        return 0;
      }

      return (
        Number(first.diameter) - Number(second.diameter) ||
        Number(first.width) - Number(second.width) ||
        Number(first.profile) - Number(second.profile)
      );
    },
    formatWheelLabel(title) {
      const parsed = this.parseMetricTireTitle(title);
      if (!parsed) {
        return "";
      }

      return `${parsed.width}/${parsed.profile} R${parsed.diameter}`;
    },
  },
};
</script>

<style lang="scss" module>
.section {
  padding: 1.5rem 1.5rem 0;
  @include tablet {
    padding: 1rem;
  }
}

.header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.title {
  font-size: 1.3125rem;
  font-weight: 600;
  margin: 0;
  line-height: 1.4;
}

.brands {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.brand {
  border: none;
  background-color: $blue;
  color: #2c2c2c;
  border-radius: 0.25rem;
  min-height: 2.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 400;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &.active {
    background-color: $blue;
    color: #2c2c2c;
  }
}

.fields {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.field {
  position: relative;
  display: flex;
  align-items: center;
  background-color: $bg-primary;
  border: 1px solid $bg-primary;
  border-radius: 0.25rem;
  min-height: 3rem;

  &.active {
    background-color: $bg-white;
    border-color: $bg-secondary;
  }

  &.disabled {
    background-color: $bg-primary;
    border-color: $bg-primary;
  }

  input {
    flex: 1 1 auto;
    border: none;
    background: transparent;
    padding: 0.875rem 5.5rem 0.875rem 1rem;
    font-size: 1rem;
    font-weight: 600;
    color: $t-primary;

    &::placeholder {
      color: $t-tertiary;
      font-weight: 400;
      font-size: 0.875rem;
      opacity: 1;
    }

    &:disabled {
      cursor: not-allowed;
      color: $t-tertiary;
    }
  }
}

.selectTrigger {
  position: absolute;
  top: 50%;
  right: 1rem;
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;

  img {
    width: 24px;
    height: 24px;
    display: block;
    flex-shrink: 0;
  }
}

.clear {
  position: absolute;
  top: 50%;
  right: 3rem;
  width: 1rem;
  height: 1rem;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  transform: translateY(-50%);

  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 1rem;
    height: 2px;
    border-radius: 999px;
    background-color: $t-primary;
  }

  &::before {
    transform: translate(-50%, -50%) rotate(45deg);
  }

  &::after {
    transform: translate(-50%, -50%) rotate(-45deg);
  }
}

.hints {
  position: absolute;
  top: calc(100% + 0.25rem);
  left: 0;
  width: 100%;
  z-index: 5;
  background-color: $bg-white;
  border: 1px solid $bg-secondary;
  border-radius: 0.25rem;
  overflow: hidden;
  box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.08);
}

.hint {
  display: block;
  width: 100%;
  border: none;
  background: transparent;
  text-align: left;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  color: $t-secondary;
  cursor: pointer;
  border-bottom: 1px solid $bg-primary;

  &:last-child {
    border-bottom: none;
  }
}

.tireSection {
  margin-top: 2.5rem;
}

.tireHeader {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem 1.5rem;
  margin-bottom: 1.25rem;
}

.tireTitle {
  font-size: 1.3125rem;
  font-weight: 600;
  margin: 0;
  line-height: 1.4;
  flex-shrink: 0;
}

.tireChips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.tireChip {
  border: none;
  background-color: $blue;
  color: #2c2c2c;
  border-radius: 100px;
  min-height: 2.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 400;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &.active {
    background-color: $blue;
    color: #2c2c2c;
  }
}

.extraChip {
  border: none;
  background-color: $bg-primary;
  color: $t-secondary;
  border-radius: 100px;
  min-height: 2.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 400;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.wheel {
  position: relative;
  height: 10.25rem;
  border-radius: 0;
  background: transparent;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  margin-bottom: -0.125rem;
  overflow: hidden;
}

.wheelWide {
  height: 11.5rem;
}

.wheelItem {
  width: 27.5rem;
}

.tireInfo {
  background-color: #c9e7fb;
  border-radius: 0.25rem;
  padding: 1rem;
  border: 1px solid $dark-blue;
  position: relative;
  z-index: 1;
}

.tireInfoText {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
  align-items: flex-start;

  p {
    margin: 0;
    font-size: 0.875rem;
    color: $t-primary;
    line-height: 1.4;
  }
}

.infoIcon {
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  border: 1px solid $dark-blue;
  background-color: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 0.1875rem;
  position: relative;

  &::before {
    content: "";
    position: absolute;
    width: 0.125rem;
    height: 0.375rem;
    border-radius: 999px;
    background-color: $dark-blue;
    top: 0.375rem;
    left: 50%;
    transform: translateX(-50%);
  }

  &::after {
    content: "";
    position: absolute;
    width: 0.125rem;
    height: 0.125rem;
    border-radius: 50%;
    background-color: $dark-blue;
    top: 0.1875rem;
    left: 50%;
    transform: translateX(-50%);
  }
}

.tirePickerBtn {
  width: 100%;
  border: none;
  border-radius: 0.25rem;
  background-color: $dark-blue;
  color: $t-white;
  padding: 0.875rem 1rem;
  min-height: 3rem;
  font-size: 0.9375rem;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
}

.tirePickerArrow {
  position: relative;
  width: 0.875rem;
  height: 0.75rem;
  flex-shrink: 0;

  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 50%;
    width: 0.375rem;
    height: 0.375rem;
    border-top: 2px solid $t-white;
    border-right: 2px solid $t-white;
    transform: translateY(-50%) rotate(45deg);
  }

  &::before {
    left: 0;
    border-top-color: $t-white;
    border-right-color: $t-white;
  }

  &::after {
    right: 0;
    border-top-color: rgba(255, 255, 255, 0.45);
    border-right-color: rgba(255, 255, 255, 0.45);
  }
}

.picker {
  margin-top: 1.25rem;
}

.pickerChips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.paramLabels {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 2.25rem;
  margin-bottom: 0.625rem;
  padding: 0 0.5rem;

  span {
    color: $t-primary;
    font-size: 0.875rem;
    text-align: center;
  }
}

.paramRow {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.paramField {
  position: relative;
  flex: 1 1 0;
}

.paramBox {
  width: 100%;
  min-height: 3rem;
  background: $bg-white;
  border: 1px solid $bg-secondary;
  border-radius: 0.25rem;
  padding: 0.75rem 1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  span {
    color: $t-primary;
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.2;
  }

  .placeholder {
    color: $t-tertiary;
  }

  &.open {
    border-color: $t-secondary;
    box-shadow: 0 0 0 1px rgba(125, 125, 125, 0.08);
  }
}

.paramDivider,
.paramLetter {
  color: $t-primary;
  font-size: 1.75rem;
  font-weight: 600;
  line-height: 1;
  padding-top: 0.75rem;
}

.paramOptions {
  position: absolute;
  bottom: calc(100% + 0.25rem);
  left: 0;
  width: 100%;
  max-height: 14rem;
  overflow-y: auto;
  z-index: 6;
  background: $bg-white;
  border: 1px solid $bg-secondary;
  border-radius: 0.25rem;
  box-shadow: 0 0.5rem 1.5rem rgba(0, 0, 0, 0.08);
  @include scrollbarY;
}

.paramOption {
  width: 100%;
  border: none;
  background: transparent;
  text-align: left;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  color: $t-secondary;
  cursor: pointer;
  border-bottom: 1px solid $bg-primary;

  &:last-child {
    border-bottom: none;
  }
}

.pickerMeta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin: 0.5rem 0 1rem;
  @include custom(410) {
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-start;
  }
}

.wideToggle {
  border: none;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.625rem;
  cursor: pointer;
  font-size: 1rem;
}

.wideToggle {
  color: $dark-blue;
  font-weight: 600;

  &.active {
    color: $orange;
  }
}

.wideMarker {
  position: relative;
  width: 1rem;
  height: 1rem;

  &::before,
  &::after {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    background: $dark-blue;
    border-radius: 999px;
    transform: translate(-50%, -50%);
  }

  &::before {
    width: 1rem;
    height: 2px;
  }

  &::after {
    width: 2px;
    height: 1rem;
  }

  &.active {
    &::before,
    &::after {
      background: $orange;
    }

    &::before {
      width: 0.875rem;
      height: 2px;
    }

    &::after {
      display: none;
    }
  }
}

.activeDark {
  background: $t-primary;
  border-color: $t-primary;
  color: $t-white;
}

.activeBlue {
  background: $dark-blue;
  border-color: $dark-blue;
  color: $t-white;
}

:global(.picker-expand-enter-active),
:global(.picker-expand-leave-active) {
  transition: opacity 0.25s ease, transform 0.25s ease, max-height 0.25s ease;
  transform-origin: top;
  overflow: hidden;
}

:global(.picker-expand-enter-from),
:global(.picker-expand-leave-to) {
  opacity: 0;
  transform: translateY(-0.5rem);
  max-height: 0;
}

:global(.picker-expand-enter-to),
:global(.picker-expand-leave-from) {
  opacity: 1;
  transform: translateY(0);
  max-height: 40rem;
}
</style>
